/**
 * Shared formatting utilities — Single Source of Truth
 *
 * Used by: Dashboard charts, Finances, Taxes, SEO pages, KPICards, etc.
 * Replaces 16 separate formatCurrency definitions across the codebase.
 */

const CURRENCY_MAP = { DE: "EUR", AT: "EUR", CH: "CHF" };
const LOCALE_MAP = { DE: "de-DE", AT: "de-AT", CH: "de-CH" };

/**
 * Format a number as currency string.
 * @param {number} amount - The amount to format
 * @param {string} country - Country code (DE, AT, CH)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, country = "DE") => {
  const currency = CURRENCY_MAP[country] || "EUR";
  const locale = LOCALE_MAP[country] || "de-DE";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount || 0);
};

/**
 * Format a number with locale-aware separators.
 * @param {number} num - The number to format
 * @param {string} country - Country code
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (num, country = "DE", decimals = 2) => {
  const locale = LOCALE_MAP[country] || "de-DE";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num || 0);
};

/**
 * Format a date string to locale-specific format.
 * @param {string|Date} dateStr - Date string or Date object
 * @param {string} country - Country code
 * @returns {string} Formatted date string
 */
export const formatDate = (dateStr, country = "DE") => {
  if (!dateStr) return "–";
  const locale = LOCALE_MAP[country] || "de-DE";
  return new Date(dateStr).toLocaleDateString(locale);
};

/**
 * Get the currency symbol for a country.
 * @param {string} country - Country code
 * @returns {string} Currency symbol (€ or CHF)
 */
export const getCurrencySymbol = (country = "DE") => {
  return country === "CH" ? "CHF" : "€";
};
